import { FaCheckCircle } from "react-icons/fa";
import * as s from "./PaymentSuccessStyled";
import BigButton from "components/atoms/buttons/BigButton";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";
import { postPlanAddPaymentComplete } from "api/planAPI";
import { useQuery } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { FiCalendar, FiDollarSign, FiPackage } from "react-icons/fi";
import { AddPaymentCompleteResponse } from "api/interfaces/planInterface.interface";
import { FiAlertCircle } from "react-icons/fi";
import Spinner from "components/atoms/loader/Spinner";
import { useMenu } from "hook/useMenu";
import { isYearlyBillingCycle } from "utils/priceUtils";

type PaymentSummary = {
  cycle: BILLING_CYCLE;
  channels: number;
  storage: number;
  channelPrice: number;
  storagePrice: number;
  isUpgrade?: boolean;
};

export default function PaymentSuccess(): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { menuNavigation, updateCurrentAccount } = useMenu({});

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id") ?? "";
  const requestId = params.get("requestId") ?? "";

  const hasStripeParams = Boolean(requestId && sessionId);

  const onReturn = () => {
    window.location.replace("/");
  };

  const onMoveUsagePlan = () => {
    updateCurrentAccount(() => {
      menuNavigation({
        to: `/subscription/usage/organization`,
        replace: true,
      });
    });
  };

  const { data, isLoading, isError } = useQuery(
    [
      "postPlanAddPaymentComplete",
      selectedAccount.accountId,
      requestId,
      sessionId,
    ],
    () =>
      postPlanAddPaymentComplete({
        accountId: selectedAccount.accountId,
        payload: {
          requestId,
          sessionId,
        },
      }),
    {
      enabled: Boolean(selectedAccount.accountId && hasStripeParams),
      retry: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    }
  ) as {
    data?: AddPaymentCompleteResponse;
    isLoading: boolean;
    isError: boolean;
  };

  if (!hasStripeParams || isError) {
    return (
      <s.Page>
        <s.Card>
          <s.ErrorBadge>
            <FiAlertCircle size={44} />
          </s.ErrorBadge>
          <s.Title>Payment Error</s.Title>
          <s.Description>
            We couldn&apos;t confirm your payment. Please try again.
          </s.Description>
          <BigButton
            label="Return to myDW"
            onClickBtn={onReturn}
            className="button"
          />
        </s.Card>
      </s.Page>
    );
  }

  if (hasStripeParams && isLoading) {
    return (
      <s.Page>
        <Spinner />
      </s.Page>
    );
  }

  const summary: PaymentSummary = data?.result
    ? {
        ...data.result,
        channelPrice:
          "channelPrice" in data.result ? data.result.channelPrice : 0,
        storagePrice:
          "storagePrice" in data.result ? data.result.storagePrice : 0,
      }
    : {
        cycle: BILLING_CYCLE.YEARLY,
        channels: 0,
        storage: 0,
        channelPrice: 0,
        storagePrice: 0,
      };

  const isYearly = isYearlyBillingCycle(summary.cycle);

  const billingLabel = isYearly ? "yearly" : "monthly";
  const billingPeriodLabel = isYearly ? "1 Year" : "Monthly";

  const totalPaid: number = isYearly
    ? summary.channels * summary.channelPrice +
      summary.storage * summary.storagePrice
    : 0;

  return (
    <s.Page>
      <s.Card>
        <s.StatusBadge>
          <FaCheckCircle size={44} />
        </s.StatusBadge>
        <s.Title>
          {summary.isUpgrade ? "Payment Confirmed" : "Payment Method Added!"}
        </s.Title>
        <s.Description>
          {summary.isUpgrade ? (
            <>
              You have successfully purchased myDW yearly channel subscription
              credits.
            </>
          ) : (
            <>
              Your credit card has been added successfully.
              <br /> You&apos;re all set to start adding end-user organizations
              for usage-based billing.
            </>
          )}
        </s.Description>

        {isYearly ? (
          <s.NextCard>
            <s.SummaryHeader>
              <FiPackage />
              <h2>Plan Summary</h2>
            </s.SummaryHeader>

            <s.SummaryRow>
              <span>Channel Monitoring</span>
              <span>
                {summary.channels} channels · {billingLabel}
              </span>
            </s.SummaryRow>

            {summary.storage > 0 && (
              <s.SummaryRow>
                <span>Cloud Storage</span>
                <span>
                  {summary.storage} TB · {billingLabel}
                </span>
              </s.SummaryRow>
            )}

            <s.Divider />

            <s.SummaryMeta>
              <s.MetaRow>
                <div className="label">
                  <FiCalendar />
                  <span>Billing Period</span>
                </div>
                <span className="value">{billingPeriodLabel}</span>
              </s.MetaRow>

              <s.MetaRow>
                <div className="label">
                  <FiDollarSign />
                  <span>Total Paid</span>
                </div>
                <span className="value strong">${totalPaid.toFixed(2)}</span>
              </s.MetaRow>
            </s.SummaryMeta>
          </s.NextCard>
        ) : (
          <s.NextCard>
            <h2>What&apos;s Next?</h2>
            <ul>
              <li>Add end user organizations from the organizations page.</li>
              <li>
                Activate end user organizations as needed from the subscriptions
                page.
              </li>
              <li>
                You&apos;ll be billed for end user channel usage and storage
                owned at the end of each billing cycle.
              </li>
            </ul>
          </s.NextCard>
        )}

        <BigButton
          label="Go to Usage Plan"
          onClickBtn={onMoveUsagePlan}
          className="button"
        />
      </s.Card>
    </s.Page>
  );
}
